import React from 'react';
import { Link } from 'react-router-dom';
import { careers } from '../mockData';
import { MapPin, Briefcase, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Careers = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#2a1c93] text-white py-24 relative overflow-hidden">

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">

          {/* LEFT CONTENT */}
          <div className="z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Unleash Your Full Potential
            </h1>

            <p className="text-lg md:text-xl text-indigo-100 mb-8">
              Join TECHSPR and be part of a team that's transforming the world
              through technology. Work on exciting projects, collaborate with
              brilliant minds, and grow your career.
            </p>
            <div className="flex gap-10 flex-wrap">
              <a
                href="#openings"
                className="inline-block mt-8 px-8 py-3 bg-white text-[#2a1c93] font-semibold rounded-lg hover:bg-indigo-100 transition duration-300"
              >
                View Openings →
              </a>
            </div>
          </div>

          {/* RIGHT IMAGE */}

          {/* RIGHT IMAGE */}
          <div className="hidden lg:block absolute right-10 bottom-0">

            {/* Glow */}
            <div className="absolute right-56 bottom-20 w-[420px] h-[420px] bg-purple-400/30 blur-3xl rounded-full"></div>

            {/* Image */}
            <img
              src="/images/girl-career.png"
              alt="career"
              className="relative right-56  h-[520px] xl:h-[650px] 2xl:h-[500px] object-contain"
            />

          </div>

        </div>

      </section>

      {/* Why Join Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Join TECHSPR?</h2>
            <p className="text-xl text-gray-600">More than just a job - a career that matters</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto bg-[#4F46E5] rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">💡</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Innovation Culture</h3>
              <p className="text-gray-600">Work with cutting-edge technologies and contribute to groundbreaking solutions</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto bg-[#4F46E5] rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">🚀</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Career Growth</h3>
              <p className="text-gray-600">Clear career paths, continuous learning, and leadership opportunities</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto bg-[#4F46E5] rounded-xl flex items-center justify-center mb-6">
                <span className="text-3xl">🌍</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Global Impact</h3>
              <p className="text-gray-600">Make a difference working with Fortune 500 clients worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="openings" className="py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-xl text-gray-600">Find your next opportunity</p>
          </div>

          <div className="space-y-6">
            {careers.map((job) => (
              <a
                key={job.id}
                href={job.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-200">
                      {job.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{job.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{job.department}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>{job.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="flex items-center text-indigo-600 font-medium group-hover:translate-x-2 transition-transform duration-200">
                      <span>View Details</span>
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2B1C93] to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Don't See the Right Role?</h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-indigo-600 hover:bg-gray-100 px-8 rounded-xl shadow-xl"
          >
            <Link to="/contact">Contact Talent Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Careers;